import { createApi } from "@reduxjs/toolkit/query/react";
import {
  GetReisiftIntegrationParams,
  ReiSiftIntegration,
  ReisiftIntegrationPayload,
  ReiSiftIntegrationSummary,
} from "@shared/interfaces";
import { showHttpErrorNotification, showProgress } from "@shared/utils";
import Methods from "@shared/constants/methods";
import { actions as sharedActions } from "@shared/store";
import { axiosBaseQuery } from "@shared/utils/request";

import config from "../../config";

export const reiSiftIntegrationApi = createApi({
  reducerPath: "reiSiftIntegrationApi",
  baseQuery: axiosBaseQuery({
    baseUrl: config.baseApiUrl,
  }),
  tagTypes: ["reisift", "integration", "summary", "get", "update", "delete"],
  endpoints: (build) => ({
    getReiSiftIntegrationSummary: build.query<ReiSiftIntegrationSummary, GetReisiftIntegrationParams>({
      providesTags: ["integration", "summary"],
      query: (params) => ({
        url: "integrations/reisift/summary",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
      },
    }),
    getReiSiftIntegration: build.query<ReiSiftIntegration, GetReisiftIntegrationParams>({
      providesTags: ["integration", "get"],
      query: (params) => ({
        url: "integrations/reisift/",
        params,
      }),
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          showProgress(dispatch, queryFulfilled);
        } catch (error) {
          console.error("Failed to get integration:", error);
        }
      },
    }),
    updateReiSiftIntegration: build.mutation<ReiSiftIntegration, ReisiftIntegrationPayload>({
      query: (data: ReisiftIntegrationPayload) => ({
        url: "integrations/reisift",
        method: Methods.PUT,
        body: data,
      }),
      invalidatesTags: ["integration", "get", "summary", "update"],
      onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then((result) => {
            dispatch(
              reiSiftIntegrationApi.util.updateQueryData(
                "getReiSiftIntegration",
                { account_id: arg.account_id },
                () => result.data,
              ),
            );
            dispatch(
              sharedActions.showNotification({
                message: "REISift integration settings were successfully updated.",
                appearance: "success",
              }),
            );
          })
          .catch(({ error }) => {
            const message = typeof error?.data === "string" ? error.data : undefined;

            const formattedError = {
              ...error,
              message,
              customMessage: !!message,
            };

            dispatch(
              showHttpErrorNotification(
                formattedError,
                "Connection failed. Please try again or use manual connection flow.",
              ),
            );
          });
      },
    }),
    deleteReiSiftIntegration: build.mutation<{ success: boolean }, number>({
      query: (account_id) => ({
        url: "integrations/reisift/",
        method: Methods.DELETE,
        params: { account_id },
      }),
      invalidatesTags: ["integration", "get", "summary"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(() => {
            dispatch(reiSiftIntegrationApi.util.resetApiState());

            dispatch(
              sharedActions.showNotification({
                message: "Your REISift account has been successfully disconnected.",
                appearance: "success",
              }),
            );
          })
          .catch((error) => {
            console.error("Failed to delete integration:", error);
          });
      },
    }),
  }),
});

export const {
  useLazyGetReiSiftIntegrationQuery,
  useLazyGetReiSiftIntegrationSummaryQuery,
  useUpdateReiSiftIntegrationMutation,
  useDeleteReiSiftIntegrationMutation,
  useGetReiSiftIntegrationQuery,
} = reiSiftIntegrationApi;
